import {
  useInfiniteQuery,
  useMutation,
  useQuery as useQuery_temp,
  useQueryClient,
  UseQueryOptions,
  QueryFunctionContext,
} from '@tanstack/react-query';
import i18n from '@ohif/i18n';
import { message } from 'antd';
import { AxiosError, AxiosResponse } from 'axios';
import { apiClient as api } from '../apiHelper';

const TOAST_NS = 'Toast';

function resolveToastMessage(
  keyOrText: string | undefined,
  error?: unknown
): string | undefined {
  if (!keyOrText) {
    return undefined;
  }

  if (error && typeof error === 'object' && error !== null) {
    const apiError = error as APIErrorResponse;
    if (apiError.error) {
      const errorKey = `users.errors.${apiError.error}`;
      const translated = i18n.t(errorKey, {
        ns: TOAST_NS,
        defaultValue: '',
      });
      if (translated) {
        return translated;
      }
    }
    if (typeof apiError.message === 'string' && apiError.message.trim()) {
      return apiError.message;
    }
  }

  return i18n.t(keyOrText, { ns: TOAST_NS, defaultValue: keyOrText });
}

type Updater<T, S> = (oldData: T | undefined, newData: S) => T;
type MutationFn<TData = unknown> = (data: TData) => Promise<any>;
type UpdaterFn = () => void;
export type QueryKeyT = [string, object | undefined] | [string] | any;
type MutationQueryKey = string[];
type MutationQueryKeyInput = MutationQueryKey | MutationQueryKey[];

interface APIErrorResponse {
  message: string;
  status_code: number;
  error: string;
  details: {
    sop_instance_uid?: string;
    original_study_info?: string;
    [key: string]: any;
  };
}

export interface GetInfinitePagesInterface<T> {
  nextId?: number;
  previousId?: number;
  data: T;
  count: number;
}
interface QueryConfig {
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  retry?: number;
  [key: string]: any;
}

// Add default configurations for queries
const DEFAULT_QUERY_CONFIG: QueryConfig = {
  // staleTime: 5000,
  refetchOnWindowFocus: false,
  retry: 0,
};

const normalizeMutationQueryKeys = (
  queryKey: MutationQueryKeyInput
): MutationQueryKey[] => {
  if (queryKey.length === 0) {
    return [];
  }

  return Array.isArray(queryKey[0])
    ? (queryKey as MutationQueryKey[])
    : [queryKey as MutationQueryKey];
};

export function useGenericMutation<T, S = any>(
  mutationFn: MutationFn<T>, // Provide the type argument for MutationFn
  queryKey: MutationQueryKeyInput,
  errorMsg: undefined | string,
  successMsg: undefined | string,
  updater: UpdaterFn = () => {},
  onSuccess?: (response: AxiosResponse<S>, data: T | S) => void,
  onError?: (error: any, data: T | S) => void,
  notification: boolean = true
) {
  const queryClient = useQueryClient();
  const queryKeys = normalizeMutationQueryKeys(queryKey);

  return useMutation({
    mutationFn,
    onMutate: async (data: T) => {
      await Promise.all(
        queryKeys.map((currentQueryKey) =>
          queryClient.cancelQueries({ queryKey: currentQueryKey })
        )
      );

      return queryKeys.map((currentQueryKey) => ({
        queryKey: currentQueryKey,
        data: queryClient.getQueryData(currentQueryKey),
      }));
    },
    onSuccess: (response, data) => {
      if (onSuccess) {
        onSuccess(response, data);
      }
      if (notification && successMsg) {
        const text = resolveToastMessage(successMsg);
        if (text) {
          message.success(text);
        }
      }
    },

    onError: (error, data, context) => {
      context?.forEach?.(
        ({
          queryKey: currentQueryKey,
          data: previousData,
        }: {
          queryKey: MutationQueryKey;
          data: unknown;
        }) => {
          queryClient.setQueryData(currentQueryKey, previousData);
        }
      );

      if (onError) {
        onError(error, data);
      }
      if (notification && errorMsg) {
        const text = resolveToastMessage(errorMsg, error);
        if (text) {
          message.error(text);
        }
      }
    },
    onSettled: () => {
      queryKeys.forEach((currentQueryKey) => {
        queryClient.invalidateQueries({ queryKey: currentQueryKey });
      });
    },
  });
}

export const useGenericMutation_old = <T, S>(
  func: (data: T | S) => Promise<AxiosResponse<S>>,
  url: string,
  params?: object,
  updater?: Updater<T, S>,
  queryKey?: QueryKeyT,
  onSuccess?: (response: AxiosResponse<S>, data: T | S) => void,
  onError?: (error: AxiosError<S>, data: T | S) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<S>, AxiosError<S>, T | S>({
    mutationFn: func,

    onMutate: async (data) => {
      const key = queryKey || [url!, params];
      await queryClient.cancelQueries({ queryKey: key });
      const previousData = queryClient.getQueryData(key);

      if (updater) {
        queryClient.setQueryData<T>(key, (oldData) => {
          if (Array.isArray(oldData)) {
            return updater(oldData, data as S);
          } else {
            return updater([] as unknown as T, data as S);
          }
        });
      }

      return previousData;
    },

    onSuccess: (response, data) => {
      if (onSuccess) {
        onSuccess(response, data);
      }
    },

    onError: (error, data, context) => {
      console.log('============ Mutation error:', error);
      console.log('============ Mutation data:', data);
      const key = queryKey || [url!, params];
      queryClient.setQueryData(key, context);

      if (onError) {
        onError(error, data);
      }
    },

    onSettled: () => {
      const key = queryKey || [url!, params];
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

export function useFetch<TData = unknown>(
  queryFn: () => Promise<TData>,
  queryKey: string[] | null,
  config: QueryConfig = {}
) {
  return useQuery_temp({
    queryKey: queryKey,
    queryFn: queryFn,
    ...DEFAULT_QUERY_CONFIG,
    ...config,
  });
}

export function useInfiniteFetch<TData = unknown>(
  queryFn: (pageParam: number) => Promise<TData>,
  queryKey: string[],
  getNextPageParam: (lastPage: TData) => number | null,
  initialPageParam: number,
  config: QueryConfig = {}
) {
  return useInfiniteQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    getNextPageParam: getNextPageParam,
    initialPageParam: initialPageParam,
    ...DEFAULT_QUERY_CONFIG,
    ...config,
  });
}

export function useCreate<TData = unknown>(
  createFn: MutationFn<TData>,
  queryKey: MutationQueryKeyInput,
  errorMsg: string = 'Error while creating new entity',
  successMsg: string = 'Entity created successfully',
  onSuccess?: (response: unknown, data: TData) => void,
  onError?: (responce: unknown, error: Error) => void,
  notification: boolean = true
) {
  return useGenericMutation<TData, any>(
    createFn,
    queryKey,
    errorMsg,
    successMsg,
    () => {},
    onSuccess,
    onError,
    notification
  );
}

export function useEdit<TData = unknown>(
  editFn: MutationFn<TData>,
  queryKey: MutationQueryKeyInput,
  errorMsg: string = 'Error while editing entity',
  successMsg: string = 'Entity edited successfully',
  onSuccess?: (response: unknown, data: TData) => void,
  onError?: (responce: unknown, error: Error) => void,
  notification: boolean = true
) {
  return useGenericMutation<TData, any>(
    editFn,
    queryKey,
    errorMsg,
    successMsg,
    () => {},
    onSuccess,
    onError,
    notification
  );
}

export function useDelete<TData = unknown>(
  deleteFn: MutationFn<TData>,
  queryKey: MutationQueryKeyInput,
  errorMsg: string = 'Error while deleting entity',
  successMsg: string = 'Entity deleted successfully',
  notification: boolean = true
) {
  return useGenericMutation<TData, any>(
    deleteFn,
    queryKey,
    errorMsg,
    successMsg,
    () => {},
    undefined,
    undefined,
    notification
  );
}

export function usePaginated<TData = unknown>(
  getPaginatedFn: (page: number) => Promise<TData>,
  queryKey: string[],
  page: number,
  config: QueryConfig = {}
) {
  return useFetch(() => getPaginatedFn(page), queryKey, {
    ...DEFAULT_QUERY_CONFIG,
    ...config,
  });
}

export function useInfinite<TData = unknown>(
  getInfiniteUsersFn: (pageParam: number) => Promise<TData>,
  queryKey: string[],
  getNextPageParam: (lastPage: TData) => number | null,
  config: QueryConfig = {}
) {
  return useInfiniteFetch(
    getInfiniteUsersFn,
    queryKey,
    getNextPageParam,
    1,
    config
  );
}

export function useSingleFetch<TData = unknown>(
  getSingleFn: (id?: string | number) => Promise<TData>,
  queryKey: string[] | null,
  id?: string | number,
  config: QueryConfig = {}
) {
  return useFetch(() => getSingleFn(id), queryKey, config);
}

export const useFetch_old = <T>(
  url: string | null,
  params?: object,
  config?: UseQueryOptions<T, Error, T, QueryKeyT> | any,
  queryKey?: QueryKeyT
) => {
  const context = useQuery_temp<T, Error, T, QueryKeyT>({
    queryKey: [url || '', params, queryKey],
    queryFn: async ({ queryKey }) => {
      const [url, params] = queryKey;
      const response = await fetcher_old({ queryKey: [url, params] });
      return response;
    },
    ...DEFAULT_QUERY_CONFIG,
    ...config,
  });

  return context;
};

export const fetcher = async <T>({
  queryKey,
  pageParam, // to use after for loading more
  signal, // to use after
}: QueryFunctionContext<QueryKeyT>): Promise<any> => {
  const [url, params] = queryKey;
  try {
    const response = await api.get(url, params);

    return response;
  } catch (error) {
    console.log('=========== fetcher error', error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const fetcher_old = async ({
  queryKey,
}: {
  queryKey: [string, object | undefined];
}) => {
  const [url, params] = queryKey;
  if (!url) {
    return null;
  }

  try {
    const response = params
      ? await api.get(url, { params })
      : await api.get(url);
    return response;
    // Check if the response has a data property that contains the actual data
    const responseData = response.data?.data || response.data;
    console.log('======== Fetcher Fetcher Processed Response:', responseData);
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const usePost = <T, S>(
  url: string,
  params?: object,
  updater?: Updater<T, S>,
  onSuccess?: (response: AxiosResponse<S>, data: T | S) => void,
  onError?: (error: AxiosError<S>, data: T | S) => void
) => {
  const apiPost: (data: T) => Promise<AxiosResponse<S>> = async (data) => {
    try {
      const response = await api.post(url, data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  return useGenericMutation_old<T, S>(
    apiPost,
    url,
    params,
    updater,
    undefined,
    onSuccess,
    onError
  );
};

export function useGenericMutation_merged<
  TData = unknown,
  TResponse = any,
  TError = APIErrorResponse,
>(
  mutationFn: (data: TData) => Promise<TResponse>,
  queryKey: string[],
  errorMsg: string = 'Error while performing mutation',
  successMsg: string = 'Mutation performed successfully',
  updater?: (oldData: any, newData: TData) => any,
  onSuccess?: (response: TResponse, data: TData) => void,
  onError?: (error: TError, data: TData) => void
) {
  const queryClient = useQueryClient();

  return useMutation<TResponse, TError, TData>({
    mutationFn,

    onMutate: async (data: TData) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousData = queryClient.getQueryData(queryKey);

      // Apply optimistic update if updater is provided
      if (updater) {
        queryClient.setQueryData(queryKey, (oldData: any) => {
          return updater(oldData, data);
        });
      }

      return previousData;
    },

    onSuccess: (response, data) => {
      if (onSuccess) {
        onSuccess(response, data);
      }
      if (successMsg) {
        message.success(successMsg);
      }
    },

    onError: (error, data, context) => {
      // Rollback to previous state
      queryClient.setQueryData(queryKey, context);

      if (onError) {
        onError(error, data);
      }

      // Use API error message if available, fallback to default error message
      const apiError = error as APIErrorResponse;
      const displayMessage = apiError?.message || errorMsg;
      message.error(displayMessage);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
    },
  });
}

export const usePost_old = <T, S>(
  url: string,
  params?: object,
  updater?: Updater<T, S>,
  onSuccess?: (response: AxiosResponse<S>, data: T | S) => void,
  onError?: (error: any, data: T | S) => void
) => {
  const apiPost: (data: T) => Promise<AxiosResponse<S>> = (data) =>
    api.post(url, data);

  return useGenericMutation_old<T, S>(
    apiPost,
    url,
    params,
    updater,
    undefined,
    onSuccess,
    onError
  );
};

export const usePut_old = <T, S>(
  url: string,
  params?: object,
  updater?: Updater<T, S>,
  queryKey?: QueryKeyT
) => {
  const apiPut: (data: T) => Promise<AxiosResponse<S>> = (data) =>
    api.put(url, data);
  return useGenericMutation_old<T, S>(apiPut, url, params, updater, queryKey);
};

export const useLoadMore = <T>(url: string | null, params?: object) => {
  const context = useInfiniteQuery<
    GetInfinitePagesInterface<T>,
    Error,
    GetInfinitePagesInterface<T>,
    QueryKeyT
  >({
    queryKey: [url || '', params],
    queryFn: fetcher,
    getNextPageParam: (lastPage, pages) => lastPage.nextId,
    getPreviousPageParam: (lastPage, pages) => lastPage.previousId,
    initialPageParam: null,
  });

  return context;
};

export function useQuery<TData = unknown>(
  queryFn: () => Promise<TData>,
  queryKey: string[] | null,
  config: QueryConfig = {}
) {
  return useQuery_temp({
    queryKey: queryKey,
    queryFn: queryFn,
    ...DEFAULT_QUERY_CONFIG,
    ...config,
  });
}
